import { useMemo } from "react";
import { useToast as useChakraToast, ToastId, UseToastOptions } from "@chakra-ui/react";
import { CustomAlert } from "../Alert/CustomAlert";

/**
 * GEN-3646 — porta única dos avisos flutuantes (toasts) do sistema. Mesma API do `useToast` do
 * Chakra (drop-in: `toast(...)`, `toast.close/closeAll/update/isActive/promise`), com as regras
 * aplicadas num lugar só em vez de em cada uma das ~500 chamadas:
 *
 * 1. Duração pela intenção (Nielsen #1/#4): antes eram 28 valores diferentes e 30 erros sumiam em
 *    ≤3s, antes de dar tempo de ler. Valor abaixo de LONGO_PROPOSITAL vira o padrão do status; a
 *    partir dele é escolha deliberada (ex.: aviso com "Desfazer") e fica. `null` (progresso) fica.
 * 2. Posição: canto superior direito. Embaixo no centro o aviso cobria a paginação das tabelas e os
 *    botões do rodapé dos modais.
 * 3. No máximo MAX_VISIVEIS na tela: o mais antigo sai quando chega um novo.
 *
 * Aviso novo deve usar `notify()` (abaixo): título e corpo como texto, sem HTML, sem repetição.
 * O ESLint proíbe importar `useToast` do Chakra fora deste arquivo.
 */
export const TOAST_DURATION = {
  success: 4000,
  info: 6000,
  warning: 6000,
  error: 8000,
} as const;

export const LONGO_PROPOSITAL = 8000;
export const MAX_VISIVEIS = 3;
const TETO_DE_LEITURA = 12000;
const PALAVRAS_SEM_ACRESCIMO = 12;
const MS_POR_PALAVRA = 300;

/** Duração final de um aviso. `palavras` (só o `notify` sabe) acrescenta tempo de leitura a texto longo. */
export const duracaoDoAviso = (
  status: UseToastOptions["status"],
  pedida: UseToastOptions["duration"],
  palavras = 0
): number | null => {
  if (pedida === null) return null;
  if (status === "loading") return pedida ?? null;
  if (typeof pedida === "number" && pedida >= LONGO_PROPOSITAL) return pedida;
  const base: number = TOAST_DURATION[status as keyof typeof TOAST_DURATION] ?? TOAST_DURATION.info;
  const leitura = Math.max(0, palavras - PALAVRAS_SEM_ACRESCIMO) * MS_POR_PALAVRA;
  return Math.min(Math.max(base, base + leitura), Math.max(base, TETO_DE_LEITURA));
};

// Fila global (os avisos do Chakra são globais): ids na ordem em que apareceram.
// Aviso de progresso (sem prazo) nunca é o que sai pelo limite: a operação ainda roda e o resultado chega
// depois por `update` no mesmo id — tirá-lo da tela fazia o resultado sumir.
type Vaga = { id: ToastId; fechar: () => void; progresso: boolean };
const visiveis: Vaga[] = [];
// Cada aviso libera a PRÓPRIA vaga ao fechar (por identidade, não por id): o onCloseComplete do Chakra roda
// depois de o aviso sair do estado, e um aviso novo que reusou o id nesse intervalo perderia a vaga dele.
const liberar = (vaga: Vaga | undefined) => {
  const i = vaga ? visiveis.indexOf(vaga) : -1;
  if (i >= 0) visiveis.splice(i, 1);
};
// Progresso é "carregando" sem prazo (ou sem status, por cautela). Erro que fica até fechar também não tem
// prazo, mas não é progresso: continua sujeito ao limite — senão erros repetidos passavam de três na tela.
const ehProgresso = (status: UseToastOptions["status"], duration: number | null) =>
  duration === null && (status === "loading" || status === undefined);
const sair = (id: ToastId) => {
  const i = visiveis.findIndex((v) => v.id === id);
  if (i >= 0) visiveis.splice(i, 1);
};
// Quantidade de palavras de um aviso do `notify`, para o tempo de leitura (chave = objeto de opções).
const palavrasDoAviso = new WeakMap<UseToastOptions, number>();

/** Só para testes: zera a fila entre um caso e outro. */
export const __zerarFilaDeAvisos = () => visiveis.splice(0, visiveis.length);

export const useToast = () => {
  const chakra = useChakraToast();

  return useMemo(() => {
    const toast = (options: UseToastOptions = {}): ToastId => {
      // O Chakra não deduplica por id: reenviar o mesmo id empilhava uma segunda cópia na tela.
      if (options.id !== undefined && chakra.isActive(options.id)) {
        atualizar(options.id, options);
        return options.id;
      }
      const onCloseComplete = options.onCloseComplete;
      const duration = duracaoDoAviso(options.status, options.duration, palavrasDoAviso.get(options));
      // `vaga` é lida no onCloseComplete, que só roda depois da atribuição.
      let vaga: Vaga | undefined;
      const id: ToastId = chakra({
        ...options,
        position: "top-right",
        duration,
        onCloseComplete: () => {
          liberar(vaga);
          onCloseComplete?.();
        },
      });
      sair(id); // o mesmo id reenviado não ocupa duas vagas
      vaga = { id, fechar: () => chakra.close(id), progresso: ehProgresso(options.status, duration) };
      visiveis.push(vaga);
      while (visiveis.length > MAX_VISIVEIS) {
        // o aviso que acabou de chegar nunca é o que sai (com 3 progressos na tela ele era o único candidato)
        const i = visiveis.findIndex((v) => !v.progresso && v.id !== id);
        if (i < 0) break; // só sobrou progresso: nenhum sai
        visiveis.splice(i, 1)[0].fechar();
      }
      return id;
    };

    // Update que só troca o conteúdo mantém a duração corrente; trocando status ou duração (ex.:
    // "Movendo…" → sucesso), aplica a regra — senão o aviso herdaria o `null` do progresso e não fecharia.
    function atualizar(id: ToastId, opcoes: UseToastOptions) {
      // O update do Chakra troca o onCloseComplete inteiro: sem embrulhar, o id nunca sairia da fila.
      const onCloseComplete = opcoes.onCloseComplete;
      const atual = visiveis.find((v) => v.id === id);
      const options: UseToastOptions = onCloseComplete
        ? {
            ...opcoes,
            onCloseComplete: () => {
              liberar(atual);
              onCloseComplete();
            },
          }
        : opcoes;
      if (palavrasDoAviso.has(opcoes)) palavrasDoAviso.set(options, palavrasDoAviso.get(opcoes) ?? 0);
      if (options.status !== undefined || options.duration !== undefined) {
        const duration = duracaoDoAviso(options.status, options.duration, palavrasDoAviso.get(options));
        const vaga = visiveis.find((v) => v.id === id);
        // update que só muda a duração mantém o status atual (ele não vem nas opções)
        if (vaga) vaga.progresso = options.status === undefined ? vaga.progresso && duration === null : ehProgresso(options.status, duration);
        chakra.update(id, { ...options, duration });
        return;
      }
      chakra.update(id, options);
    }

    return Object.assign(toast, {
      close: chakra.close,
      closeAll: chakra.closeAll,
      isActive: chakra.isActive,
      promise: chakra.promise,
      update: (id: ToastId, options: Omit<UseToastOptions, "id">) => {
        // O aviso já saiu da tela (o usuário fechou o "Movendo…"): o resultado chega como aviso novo,
        // em vez de um update silencioso num id que não existe mais.
        if (!chakra.isActive(id)) {
          // eslint-disable-next-line no-restricted-syntax -- só entra aqui com `render` (checado na mesma linha)
          if (options.render) {
            const novo = { ...options, id };
            // a cópia é outro objeto: leva junto as palavras, senão o resultado perde o tempo de leitura
            if (palavrasDoAviso.has(options)) palavrasDoAviso.set(novo, palavrasDoAviso.get(options) ?? 0);
            toast(novo);
          }
          return;
        }
        atualizar(id, options);
      },
    });
  }, [chakra]);
};

export type AppToast = ReturnType<typeof useToast>;

export type NotifyStatus = "success" | "error" | "warning" | "info" | "loading";

export interface NotifyOptions {
  status: NotifyStatus;
  /** O que aconteceu, em poucas palavras ("Grupo criado", "Não foi possível carregar os usuários"). */
  title: string;
  /** Contexto ou próximo passo. Texto puro — nunca a mensagem crua do servidor. */
  description?: string;
  /** Ação no próprio aviso ("Tentar novamente"). Erro com ação fica até o usuário fechar. */
  action?: { text: string; onClick: () => void };
  /** Só para progresso (`null`) ou exceção deliberada; o padrão vem do status. */
  duration?: number | null;
  /** Para atualizar o aviso depois (`notify.update`). Reenviar o mesmo id atualiza o aviso em vez de duplicar. */
  id?: ToastId;
}

const opcoesDoAviso = (o: NotifyOptions) => {
  const options: UseToastOptions = {
    id: o.id,
    status: o.status,
    isClosable: true,
    // Erro com ação pede uma decisão do usuário: não some sozinho.
    duration: o.status === "error" && o.action ? null : o.status === "loading" ? o.duration ?? null : o.duration,
    render: ({ onClose }) => (
      <CustomAlert
        heading={o.title}
        body={o.description}
        clicable={
          o.action
            ? {
                // Abaixo do texto: ao lado, o link espremia a descrição numa coluna estreita.
                newLine: true,
                text: o.action.text,
                action: () => {
                  o.action?.onClick();
                  onClose();
                },
              }
            : undefined
        }
        closableFunc={onClose}
        status={o.status}
        variant="alert"
        type="three"
        className="alert-instance"
      />
    ),
  };
  palavrasDoAviso.set(options, `${o.title} ${o.description ?? ""}`.trim().split(/\s+/).length);
  return options;
};

/**
 * GEN-3646 — o jeito de mostrar um aviso. Mesmo aviso já na tela (mesmo status, título e corpo) não
 * se repete: três falhas iguais seguidas viram um aviso só.
 */
export const notify = (toast: AppToast, o: NotifyOptions): ToastId => {
  const id = o.id ?? `${o.status}:${o.title}:${o.description ?? ""}`;
  if (!o.id && toast.isActive(id)) return id;
  return toast(opcoesDoAviso({ ...o, id }));
};

/** Troca o conteúdo de um aviso já na tela (ex.: "Movendo…" → resultado), mantendo o padrão visual. */
notify.update = (toast: AppToast, id: ToastId, o: Omit<NotifyOptions, "id">) => {
  const completo = opcoesDoAviso({ ...o, id });
  const { id: _ignorado, ...options } = completo;
  // a cópia sem `id` é outro objeto: leva junto as palavras, senão o resultado perde o tempo de leitura
  palavrasDoAviso.set(options, palavrasDoAviso.get(completo) ?? 0);
  toast.update(id, options);
};
